import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Alert, Box, Button, CircularProgress, Link } from '@mui/material';
import { useRef, useState } from 'react';

import { EVIDENCE_ACCEPT, evidenceFileError } from '~/components/tasks/detail/evidenceFiles';
import { CARD_SX, CARD_TITLE_SX, EvidenceIcon } from '~/components/tasks/detail/taskSurfaces';
import { logChipStyle } from '~/components/tasks/detail/UpdateLog';
import { formatShortDate } from '~/components/tasks/taskStyle';
import type { TaskDetail } from '~/data/generated/model';
import { BUTTON_SX } from '~/theme';

function truncate(text: string, max = 80): string {
  return text.length > max ? `${text.slice(0, max).trimEnd()}…` : text;
}

const CELL_SX = {
  py: '10px',
  pr: '16px',
  borderBottom: '1px solid #eef0f3',
  fontSize: 13,
} as const;

export function TaskAttachmentsTab({
  task,
  onUpload,
  onShowUpdate,
}: {
  task: TaskDetail;
  onUpload: (file: File) => Promise<unknown>;
  onShowUpdate: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upload = async (files: FileList | null) => {
    const picked = Array.from(files ?? []);
    if (input.current) input.current.value = '';
    const invalid = picked.map(evidenceFileError).find(Boolean);
    if (invalid) {
      setError(invalid);
      return;
    }
    setError(null);
    setUploading(true);
    try {
      for (const file of picked) await onUpload(file);
    } catch {
      setError('Could not upload the file. Try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box component="section" aria-label="Attachments" sx={CARD_SX}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: '12px' }}>
        <Box sx={{ ...CARD_TITLE_SX, flex: 1 }}>ATTACHMENTS</Box>
        <Button
          size="small"
          startIcon={uploading ? <CircularProgress size={14} /> : <UploadFileIcon />}
          disabled={uploading}
          onClick={() => input.current?.click()}
          sx={BUTTON_SX}
        >
          Upload
        </Button>
        <input
          ref={input}
          type="file"
          hidden
          multiple
          accept={EVIDENCE_ACCEPT}
          data-testid="attachment-input"
          onChange={(e) => void upload(e.target.files)}
        />
      </Box>
      {error ? (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}
      {task.evidence.length === 0 ? (
        <Box sx={{ fontSize: 14, color: '#777' }}>
          No attachments yet. Upload a file here or attach one when you log an update.
        </Box>
      ) : (
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
          <Box component="thead">
            <Box component="tr" sx={{ textAlign: 'left', color: '#666', fontSize: 12 }}>
              {['File', 'Uploaded by', 'Date', 'From update'].map((label) => (
                <Box component="th" key={label} sx={{ ...CELL_SX, fontWeight: 500, fontSize: 12 }}>
                  {label}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {task.evidence.map((item) => {
              const log = task.logs.find((l) => l.id === item.logId);
              return (
                <Box component="tr" key={item.id}>
                  <Box component="td" sx={CELL_SX}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <EvidenceIcon fileType={item.fileType} size={26} />
                      <Link
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        sx={{ color: '#1b1f24', wordBreak: 'break-word' }}
                      >
                        {item.name}
                      </Link>
                    </Box>
                  </Box>
                  <Box component="td" sx={CELL_SX}>
                    {item.uploadedBy?.name ?? 'Unknown user'}
                  </Box>
                  <Box component="td" sx={{ ...CELL_SX, whiteSpace: 'nowrap' }}>
                    {formatShortDate(item.createdAt)}
                  </Box>
                  <Box component="td" sx={{ ...CELL_SX, color: '#555' }}>
                    {log ? (
                      <Link component="button" underline="hover" onClick={onShowUpdate}>
                        {logChipStyle(log).label} · {truncate(log.text, 60)}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
}
